import React, { useEffect, useState } from "react";
import { PhoneLink } from "@/utils/PhoneLink";
import {
  Box, Typography, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, InputAdornment, Drawer, Divider, Button, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";
import WriteOffButton from "@/components/zephyr/WriteOffButton";
import EditableInvAmount from "@/components/zephyr/EditableInvAmount";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmt = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

// ── Invoice Detail Modal ──────────────────────────────────────────
function InvoiceDetailModal({ open, invoiceID, claimID, onClose, onWriteOff }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  React.useEffect(() => {
    if (!open || !invoiceID) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_DETAILS}/${invoiceID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, invoiceID, reloadKey]);

  const [priceError, setPriceError] = useState(null);
  const reloadModal = () => { setReloadKey((k) => k + 1); onWriteOff?.(); };

  const c = data?.claim;
  const scripts = data?.scripts ?? [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>Invoice #{invoiceID}</span>
          {c && (
            <WriteOffButton
              claimID={c.ClaimID ?? claimID}
              invoiceID={invoiceID}
              scripts={scripts}
              onSuccess={reloadModal}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
        ) : (
          <>
            {c && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Patient</Typography>
                  <Typography variant="body2" fontWeight={600}>{c.FullName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Chemist</Typography>
                  <Typography variant="body2">{c.ChemistName || c.ChemShortCode || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Date</Typography>
                  <Typography variant="body2">{fmtDate(c.InvoiceDate)}</Typography>
                </Box>
                {c.InvComment && (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Comment</Typography>
                    <Typography variant="body2">{c.InvComment}</Typography>
                  </Box>
                )}
              </Box>
            )}
            {priceError && (
              <Alert severity="warning" onClose={() => setPriceError(null)} sx={{ mb: 1.5 }}>
                {priceError}
              </Alert>
            )}
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Item", "Qty", "Invoiced", "Status", ""].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scripts.map((s) => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell>{s.Qty ?? "—"}</TableCell>
                      <TableCell align="right">
                        <EditableInvAmount
                          script={s}
                          onSaved={() => { setPriceError(null); reloadModal(); }}
                          onError={setPriceError}
                        />
                      </TableCell>
                      <TableCell>
                        {s.PaymentID
                          ? <Chip label="Paid" size="small" sx={{ background: "#dcfce7", color: "#16a34a", fontSize: "0.72rem" }} />
                          : Number(s.WrittenOff)
                            ? <Chip label="Written Off" size="small" sx={{ background: "#fee2e2", color: "#dc2626", fontSize: "0.72rem" }} />
                            : Number(s.OnHold) === -1
                              ? <Chip label="Missed" size="small" sx={{ background: "#fef9c3", color: "#92400e", fontSize: "0.72rem" }} />
                              : null}
                      </TableCell>
                      <TableCell sx={{ px: "4px !important" }}>
                        <WriteOffButton
                          claimID={c?.ClaimID ?? claimID}
                          scriptIDs={[s.ScriptID]}
                          scripts={[s]}
                          onSuccess={reloadModal}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Next Reconciliation Modal ─────────────────────────────────────
function NextReconModal({ open, chemShortCode, chemistName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !chemShortCode) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CHEMIST_NEXT_RECON}/${chemShortCode}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, chemShortCode]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,

      }}>
        <span>Next Reconciliation — {chemistName ?? chemShortCode}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
        ) : data ? (
          <>
            <Box sx={{ display: "flex", gap: 4, mb: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Next Statement Date</Typography>
                <Typography fontWeight={700} sx={{ color: Z_PURPLE }}>{fmtDate(data.nextStatementDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Value to Pay</Typography>
                <Typography fontWeight={900} sx={{ color: "#16a34a", fontSize: "1.2rem" }}>{fmt(data.totalValue)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Scripts</Typography>
                <Typography fontWeight={700}>{data.scripts?.length ?? 0}</Typography>
              </Box>
            </Box>
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, maxHeight: 420, overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Patient", "Invoice", "Item", "Date", "Received"].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE,
                        background: `${Z_PURPLE}08` }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.scripts ?? []).map((s) => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1.5, fontSize: "0.8rem" } }}>
                      <TableCell>{s.FullName}</TableCell>
                      <TableCell>{s.InvoiceID ?? "—"}</TableCell>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                      <TableCell align="right">{fmt(s.RcvdAmount)}</TableCell>
                    </TableRow>
                  ))}
                  {data.scripts?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          No scripts pending reconciliation for this chemist.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Outstanding Scripts Modal ─────────────────────────────────────
function OutstandingScriptsModal({ open, scripts, chemistName, onClose, onOpenInvoice, onWriteOff }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
        <Box>
          <Typography fontWeight={700}>Outstanding Scripts ({scripts?.length ?? 0})</Typography>
          {chemistName && <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>{chemistName}</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, maxHeight: 480, overflowY: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { label: "Invoice", align: "left" },
                  { label: "Patient", align: "left" },
                  { label: "Item", align: "left" },
                  { label: "Date", align: "left" },
                  { label: "Amount", align: "right" },
                  { label: "", align: "left" },
                ].map(({ label, align }) => (
                  <TableCell key={label} align={align}
                    sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1, background: `${Z_PURPLE}08` }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(scripts ?? []).map((s) => (
                <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1.5, fontSize: "0.8rem" } }}>
                  <TableCell>
                    <button onClick={() => onOpenInvoice(s.InvoiceID, s.ClaimID)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                        color: Z_PURPLE, fontWeight: 700, textDecoration: "underline", fontSize: "inherit" }}>
                      #{s.InvoiceID}
                    </button>
                  </TableCell>
                  <TableCell>{s.FullName}</TableCell>
                  <TableCell>{s.txtItem}</TableCell>
                  <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                  <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                  <TableCell sx={{ px: "4px !important" }}>
                    <WriteOffButton claimID={s.ClaimID} scriptIDs={[s.ScriptID]} scripts={[s]} onSuccess={onWriteOff} />
                  </TableCell>
                </TableRow>
              ))}
              {(scripts ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No unpaid scripts.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Script Batch List Modal (with drill-into a batch's scripts) ───
function ScriptBatchesModal({ open, chemShortCode, chemistName, onClose }) {
  const [batches, setBatches] = useState(null);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null); // { ScrBatchID, ScrBatchCode, ScrBatchDate }
  const [scripts, setScripts] = useState(null);
  const [loadingScripts, setLoadingScripts] = useState(false);

  React.useEffect(() => {
    if (!open || !chemShortCode) return;
    setSelectedBatch(null);
    setScripts(null);
    setLoadingBatches(true);
    setBatches(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CHEMIST_SCRIPT_BATCHES}/${chemShortCode}`, "GET")
      .then((res) => setBatches(res?.data ?? []))
      .catch(() => setBatches([]))
      .finally(() => setLoadingBatches(false));
  }, [open, chemShortCode]);

  const openBatch = (b) => {
    setSelectedBatch(b);
    setScripts(null);
    setLoadingScripts(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_SCRIPT_BATCH}/${b.ScrBatchID}/Scripts`, "GET")
      .then((res) => setScripts(res?.data ?? []))
      .catch(() => setScripts([]))
      .finally(() => setLoadingScripts(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {selectedBatch && (
            <Tooltip title="Back to batch list">
              <IconButton size="small" onClick={() => { setSelectedBatch(null); setScripts(null); }} sx={{ color: "#fff" }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box>
            <Typography fontWeight={700}>
              {selectedBatch ? `Batch ${selectedBatch.ScrBatchCode || `#${selectedBatch.ScrBatchID}`}` : "Script Batches"}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
              {selectedBatch ? `Dispensed ${fmtDate(selectedBatch.ScrBatchDate)}` : (chemistName ?? chemShortCode)}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {/* Batch list */}
        {!selectedBatch && (
          loadingBatches ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
          ) : (
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, maxHeight: 480, overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {[{ label: "Batch Code" }, { label: "Date" }, { label: "Scripts", align: "right" }, { label: "" }].map(({ label, align }) => (
                      <TableCell key={label} align={align ?? "left"}
                        sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1, background: `${Z_PURPLE}08` }}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(batches ?? []).map((b) => (
                    <TableRow key={b.ScrBatchID} hover onClick={() => openBatch(b)}
                      sx={{ cursor: "pointer", "& td": { py: 0.7, px: 1.5, fontSize: "0.82rem" } }}>
                      <TableCell sx={{ fontWeight: 700, color: Z_PURPLE }}>{b.ScrBatchCode || `#${b.ScrBatchID}`}</TableCell>
                      <TableCell>{fmtDate(b.ScrBatchDate)}</TableCell>
                      <TableCell align="right">{b.ScriptCount ?? 0}</TableCell>
                      <TableCell align="right"><Button size="small" sx={{ textTransform: "none", color: Z_BLUE, minWidth: 0 }}>Open</Button></TableCell>
                    </TableRow>
                  ))}
                  {(batches ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No script batches for this chemist.</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {/* Scripts within a batch */}
        {selectedBatch && (
          loadingScripts ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
          ) : (
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, maxHeight: 480, overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {[{ label: "Patient" }, { label: "Date of Dispense" }, { label: "Item" }, { label: "Amount Paid", align: "right" }].map(({ label, align }) => (
                      <TableCell key={label} align={align ?? "left"}
                        sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1, background: `${Z_PURPLE}08` }}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(scripts ?? []).map((s) => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1.5, fontSize: "0.8rem" } }}>
                      <TableCell>{s.FullName}</TableCell>
                      <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell align="right">{s.RcvdAmount != null ? fmt(s.RcvdAmount) : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {(scripts ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No scripts in this batch.</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Chemist Drawer ────────────────────────────────────────────────
function ChemistDrawer({ open, chemShortCode, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [bsb, setBsb] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [savingBank, setSavingBank] = useState(false);
  const [invoiceDetailID, setInvoiceDetailID] = useState(null);
  const [invoiceDetailClaimID, setInvoiceDetailClaimID] = useState(null);
  const [nextReconOpen, setNextReconOpen] = useState(false);
  const [outstandingOpen, setOutstandingOpen] = useState(false);
  const [batchesOpen, setBatchesOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reloadDrawer = () => setReloadKey((k) => k + 1);

  React.useEffect(() => {
    if (!open || !chemShortCode) return;
    setLoading(true);
    setData(null);
    setEditingBank(false);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CHEMIST_DETAILS}/${chemShortCode}`, "GET")
      .then((res) => {
        const d = res?.data ?? null;
        setData(d);
        setBsb(d?.chemist?.BSB ?? "");
        setAccountNumber(d?.chemist?.AccountNumber ?? "");
      })
      .finally(() => setLoading(false));
  }, [open, chemShortCode, reloadKey]);

  const saveBank = async () => {
    setSavingBank(true);
    await phappsRequest(
      `${API_PATHS.ZEPHYR_ADMIN_UPDATE_CHEMIST_BANK}/${chemShortCode}`,
      "PUT",
      { bsb: bsb.trim() || null, accountNumber: accountNumber.trim() || null }
    ).catch(() => {});
    setSavingBank(false);
    setEditingBank(false);
    setData((prev) =>
      prev ? { ...prev, chemist: { ...prev.chemist, BSB: bsb.trim() || null, AccountNumber: accountNumber.trim() || null } } : prev
    );
  };

  const ch = data?.chemist;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: "100vw", md: 740 }, display: "flex", flexDirection: "column" } }}
      >
        {/* Header */}
        <Box sx={{
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          color: "#fff", px: 2.5, py: 1.5, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Box>
            <Typography fontWeight={800} fontSize="1rem">
              {ch?.ChemistName ?? chemShortCode ?? "Chemist"}
            </Typography>
            {chemShortCode && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>{chemShortCode}</Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress sx={{ color: Z_PURPLE }} />
          </Box>
        ) : ch ? (
          <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2.5, p: 2.5 }}>

            {/* Contact row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              {ch.ChemistPhone && (
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  <PhoneLink phone={ch.ChemistPhone} />
                </Typography>
              )}
              {ch.ChemistEmail && (
                <Button
                  size="small"
                  startIcon={<EmailOutlinedIcon fontSize="small" />}
                  href={`mailto:${ch.ChemistEmail}`}
                  sx={{ textTransform: "none", color: Z_BLUE, p: 0, minWidth: 0, fontSize: "0.8rem" }}
                >
                  {ch.ChemistEmail}
                </Button>
              )}
            </Box>

            {/* Stats row */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box sx={{
                border: `1px solid ${Z_PURPLE}22`, borderRadius: 2, p: 2,
                background: (data.outstandingTotal ?? 0) > 0 ? "#fff5f5" : "#f0fdf4",
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Outstanding (Unpaid)</Typography>
                <Typography fontWeight={900}
                  sx={{ fontSize: "1.5rem", color: (data.outstandingTotal ?? 0) > 0 ? "#dc2626" : "#16a34a", lineHeight: 1.2, mt: 0.25 }}>
                  {fmt(data.outstandingTotal)}
                </Typography>
              </Box>
              <Box sx={{
                border: `1px solid ${Z_PURPLE}22`, borderRadius: 2, p: 2,
                background: (data.uninvoicedTotal ?? 0) > 0 ? "#fffbeb" : "#f8fafc",
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Uninvoiced Value</Typography>
                <Typography fontWeight={900}
                  sx={{ fontSize: "1.5rem", color: (data.uninvoicedTotal ?? 0) > 0 ? "#92400e" : "#64748b", lineHeight: 1.2, mt: 0.25 }}>
                  {fmt(data.uninvoicedTotal)}
                </Typography>
              </Box>
            </Box>

            {/* Bank details */}
            <Box sx={{ border: `1px solid ${Z_PURPLE}22`, borderRadius: 2, p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <AccountBalanceOutlinedIcon sx={{ color: Z_PURPLE, fontSize: 18 }} />
                <Typography variant="body2" fontWeight={700} sx={{ color: Z_PURPLE }}>Bank Details</Typography>
                {!editingBank && (
                  <Tooltip title="Edit bank details">
                    <IconButton size="small" onClick={() => setEditingBank(true)} sx={{ color: Z_PURPLE, p: 0.5, ml: 0.5 }}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {editingBank ? (
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    label="BSB"
                    size="small"
                    value={bsb}
                    onChange={(e) => setBsb(e.target.value)}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    label="Account Number"
                    size="small"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    sx={{ width: 210 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={saveBank}
                    disabled={savingBank}
                    sx={{ textTransform: "none", fontWeight: 700,
                      background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` }}
                  >
                    {savingBank ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingBank(false);
                      setBsb(ch.BSB ?? "");
                      setAccountNumber(ch.AccountNumber ?? "");
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 4 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>BSB</Typography>
                    <Typography variant="body2" fontWeight={600}>{ch.BSB || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Account Number</Typography>
                    <Typography variant="body2" fontWeight={600}>{ch.AccountNumber || "—"}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Scripts — outstanding list + batch list */}
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1.25, color: Z_PURPLE }}>Scripts</Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<AssessmentOutlinedIcon />}
                  onClick={() => setNextReconOpen(true)}
                  sx={{ textTransform: "none", borderColor: Z_PURPLE, color: Z_PURPLE }}
                >
                  Next Reconciliation Run
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ReceiptLongOutlinedIcon />}
                  onClick={() => setOutstandingOpen(true)}
                  sx={{ textTransform: "none", borderColor: Z_PURPLE, color: Z_PURPLE }}
                >
                  Outstanding Scripts ({data.outstandingScripts?.length ?? 0})
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Inventory2OutlinedIcon />}
                  onClick={() => setBatchesOpen(true)}
                  sx={{ textTransform: "none", borderColor: Z_BLUE, color: Z_BLUE }}
                >
                  Script Batch List
                </Button>
              </Box>
            </Box>
          </Box>
        ) : null}
      </Drawer>

      <InvoiceDetailModal
        open={!!invoiceDetailID}
        invoiceID={invoiceDetailID}
        claimID={invoiceDetailClaimID}
        onClose={() => setInvoiceDetailID(null)}
        onWriteOff={reloadDrawer}
      />
      <NextReconModal
        open={nextReconOpen}
        chemShortCode={chemShortCode}
        chemistName={ch?.ChemistName}
        onClose={() => setNextReconOpen(false)}
      />
      <OutstandingScriptsModal
        open={outstandingOpen}
        scripts={data?.outstandingScripts ?? []}
        chemistName={ch?.ChemistName}
        onClose={() => setOutstandingOpen(false)}
        onOpenInvoice={(invoiceID, claimID) => { setInvoiceDetailID(invoiceID); setInvoiceDetailClaimID(claimID); }}
        onWriteOff={reloadDrawer}
      />
      <ScriptBatchesModal
        open={batchesOpen}
        chemShortCode={chemShortCode}
        chemistName={ch?.ChemistName}
        onClose={() => setBatchesOpen(false)}
      />
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────
export default function ZephyrChemistsPage() {
  const [chemists, setChemists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [selectedChemShortCode, setSelectedChemShortCode] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    phappsRequest(API_PATHS.ZEPHYR_ADMIN_ZEPHYR_CHEMISTS, "GET")
      .then((res) => setChemists(res?.data ?? []))
      .catch(() => setError("Failed to load chemists."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = chemists.filter(
    (c) =>
      !filter ||
      c.ChemistName?.toLowerCase().includes(filter.toLowerCase()) ||
      c.ChemShortCode?.toLowerCase().includes(filter.toLowerCase())
  );

  const openDrawer = (chemShortCode) => {
    setSelectedChemShortCode(chemShortCode);
    setDrawerOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <img src={ZephyrIconWhite} alt="" style={{ height: 20, objectFit: "contain" }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: Z_PURPLE }}>Chemists</Typography>
      </Box>

      <TextField
        size="small"
        placeholder="Filter by name or code…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ width: 280, mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
            </InputAdornment>
          ),
        }}
      />

      {loading && <CircularProgress sx={{ color: Z_PURPLE }} />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff" }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
              <TableRow>
                {["Code", "Chemist Name", "Phone", "Outstanding", "Uninvoiced"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((c) => (
                <TableRow
                  key={c.ChemistID}
                  hover
                  onClick={() => openDrawer(c.ChemShortCode)}
                  sx={{ cursor: "pointer", "& td": { py: 0.75, px: 1.5, fontSize: "0.82rem" } }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ color: Z_PURPLE }}>{c.ChemShortCode}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{c.ChemistName}</TableCell>
                  <TableCell><PhoneLink phone={c.ChemistPhone} /></TableCell>
                  <TableCell sx={{ color: Number(c.outstandingTotal) > 0 ? "#dc2626" : "inherit", fontWeight: 700 }}>
                    {fmt(c.outstandingTotal)}
                  </TableCell>
                  <TableCell sx={{ color: Number(c.uninvoicedTotal) > 0 ? "#92400e" : "inherit", fontWeight: 600 }}>
                    {Number(c.uninvoicedTotal) > 0 ? fmt(c.uninvoicedTotal) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No chemists found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ChemistDrawer
        open={drawerOpen}
        chemShortCode={selectedChemShortCode}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
}
